import React, { useEffect, useState } from "react";
import { X, Copy, Lock, RotateCw, Trash2, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modale de gestion du lien de partage privé d'un événement (lecture seule
 * pour un assistant qui aide à la gestion du staff).
 */
export default function StaffShareModal({ eventId, eventName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [busy, setBusy] = useState(false);

  const url = token ? `${window.location.origin}/share/horaire/${token}` : "";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/share-link`);
        if (alive) {
          setToken(data.token || null);
          setGeneratedAt(data.generated_at || null);
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [eventId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/share-link`);
      setToken(data.token);
      setGeneratedAt(new Date().toISOString());
      toast.success("Lien généré");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm("Révoquer ce lien ? L'assistant n'y aura plus accès.")) return;
    setBusy(true);
    try {
      await api.delete(`/events/${eventId}/share-link`);
      setToken(null);
      setGeneratedAt(null);
      toast.success("Lien révoqué");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="staff-share-modal"
    >
      <div
        className="rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]"
          aria-label="Fermer"
          data-testid="staff-share-close-btn"
        >
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Partage privé</div>
        <h3 className="text-xl font-bold tracking-tight mb-1">
          Lien d&apos;horaire (lecture seule)
        </h3>
        <p className="text-xs text-[var(--muted)] mb-4">
          Pour « {eventName || "cet événement"} ». Le destinataire pourra voir l&apos;horaire complet
          et le staff assigné, sans pouvoir modifier ni accéder à autre chose (paies, forfaits,
          coordonnées des officiels, autres événements).
        </p>

        {loading ? (
          <div className="text-sm text-[var(--muted)]">Chargement…</div>
        ) : token ? (
          <div className="space-y-4">
            <div className="rounded-xl p-3" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              <div className="label mb-1">Lien actif</div>
              <input
                type="text"
                readOnly
                value={url}
                onFocus={(e) => e.target.select()}
                className="w-full bg-transparent text-xs font-mono break-all outline-none"
                data-testid="staff-share-url-input"
              />
              {generatedAt && (
                <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-2">
                  Généré le {new Date(generatedAt).toLocaleString("fr-CA")}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={copyUrl}
                className="btn-primary inline-flex items-center gap-1.5"
                data-testid="staff-share-copy-btn"
              >
                <Copy size={14} strokeWidth={2.4} /> Copier le lien
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost inline-flex items-center gap-1.5"
                data-testid="staff-share-open-btn"
              >
                <ExternalLink size={14} strokeWidth={2.4} /> Aperçu
              </a>
              <button
                onClick={generate}
                disabled={busy}
                className="btn-ghost inline-flex items-center gap-1.5"
                data-testid="staff-share-rotate-btn"
              >
                <RotateCw size={14} strokeWidth={2.4} /> Régénérer
              </button>
              <button
                onClick={revoke}
                disabled={busy}
                className="btn-ghost inline-flex items-center gap-1.5"
                data-testid="staff-share-revoke-btn"
              >
                <Trash2 size={14} strokeWidth={2.4} /> Révoquer
              </button>
            </div>

            <div className="rounded-xl p-3 text-xs text-[var(--muted)] flex items-start gap-2" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              <Lock size={14} strokeWidth={2.2} className="mt-0.5 shrink-0" />
              <div>
                Régénérer le lien <strong>invalide immédiatement l&apos;ancien</strong>. Le nouveau
                lien devra être renvoyé à ton assistant. <br />
                Révoquer supprime complètement l&apos;accès.
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl p-4 text-sm" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              Aucun lien n&apos;est actif pour cet événement. Génère-en un et partage-le à la
              personne qui t&apos;aide pour la gestion du staff.
            </div>
            <button
              onClick={generate}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-1.5"
              data-testid="staff-share-generate-btn"
            >
              <Eye size={14} strokeWidth={2.4} /> Générer le lien de partage
            </button>
          </div>
        )}

        <div className="flex items-center justify-end mt-5">
          <button onClick={onClose} className="btn-ghost" data-testid="staff-share-close-btn-bottom">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
